import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId, selectLoggedIn } from 'selectors/accountSelectors'
import requestMyEnrollments from 'api/loyalty/requestMyEnrollments'
import type { MyEnrollment } from 'api/loyalty/requestMyEnrollments'
import type { StatusMatchLabPayload } from 'loyalty/statusMatch/statusMatchTypes'
import useActiveStatusMatchCampaign from './useActiveStatusMatchCampaign'

export interface MergedStep {
  key: string
  title: string
  description: string
  eligible: boolean
}

export interface MyStatusMatchEnrollmentResult {
  enrollment: MyEnrollment | undefined
  config: StatusMatchLabPayload | undefined
  steps: Array<MergedStep>
  isEnrolled: boolean
  isLoading: boolean
  refetch: () => void
}

/**
 * Fetches the user's active enrollment for the current Status Match campaign.
 *
 * Flow:
 * 1. Calls GET /claims/:customerId/enrollments → all active enrollments
 * 2. Matches by campaignId to the Lab config (via useActiveStatusMatchCampaign)
 * 3. Merges BE checks (key + eligible) with Lab successSteps (key + title + description)
 *
 * Returns merged steps with eligible state + the raw enrollment for heading/countdown.
 */
function useMyStatusMatchEnrollment(): MyStatusMatchEnrollmentResult {
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const customerId = useAppSelector(state => getCurrentUserId(state))
  const statusMatch = useActiveStatusMatchCampaign()
  const [enrollments, setEnrollments] = useState<Array<MyEnrollment>>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetch = useCallback(async() => {
    if (!isLoggedIn || !customerId) return
    setIsLoading(true)
    try {
      const result = await requestMyEnrollments(customerId)
      setEnrollments(result)
    } catch {
      setEnrollments([])
    } finally {
      setIsLoading(false)
    }
  }, [isLoggedIn, customerId])

  useEffect(() => {
    fetch()
  }, [fetch])

  // Find the enrollment matching the Lab campaign
  const enrollment = useMemo(() => {
    if (!statusMatch) return undefined
    return enrollments.find(e => e.campaignId === statusMatch.config.campaignId)
  }, [enrollments, statusMatch])

  // Merge BE checks with Lab successSteps by key
  const steps = useMemo<Array<MergedStep>>(() => {
    if (!enrollment || !statusMatch?.config.successSteps) return []
    return statusMatch.config.successSteps.map(step => {
      const check = enrollment.checks.find(c => c.key === step.key)
      return {
        key: step.key,
        title: step.title,
        description: step.description,
        eligible: check?.eligible ?? false,
      }
    })
  }, [enrollment, statusMatch])

  return {
    enrollment,
    config: statusMatch?.config,
    steps,
    isEnrolled: !!enrollment,
    isLoading,
    refetch: fetch,
  }
}

export default useMyStatusMatchEnrollment
